import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import Stepper, { STEPS } from "../components/shared/Stepper";
import LeadsStep from "../components/campaignWizard/LeadsStep";
import SequenceStep from "../components/campaignWizard/SequenceStep";
import SendersStep from "../components/campaignWizard/SendersStep";
import ScheduleStep from "../components/campaignWizard/ScheduleStep";
import PreviewStep from "../components/campaignWizard/PreviewStep";
import { validateStepsClient } from "../components/campaignWizard/sequenceUtils";
import {
  createCampaignPromise,
  updateCampaignPromise,
  getCampaignByIdPromise,
  createSequencePromise,
  updateSequencePromise,
  startCampaignPromise,
  getLeadListsPromise,
  getLinkedInAccounts,
} from "../services/apiService";
import { sanitizeSequenceStepForApi } from "../components/campaignWizard/sequenceUtils";
import { notifyError } from "../helpers/apiError";

const DEFAULT_STATE = {
  name: "",
  type: "MIXED",
  leadListIds: [],
  leadListId: null,
  excludeListId: null,
  excludeOptions: {
    excludeOtherCampaigns: true,
    excludeMessagedByOtherSenders: true,
    excludeSameSenderOtherCampaigns: true,
  },
  steps: [],
  sequenceId: null,
  linkedinAccountIds: [],
  linkedinAccountId: null,
  primaryLinkedInAccountId: null,
  startDate: new Date().toISOString(),
  endDate: null,
  timezone: "Europe/London",
  activeHoursStart: "09:00",
  activeHoursEnd: "19:00",
  activeDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  dailyLimit: 30,
};

export default function CampaignWizard() {
  const { lng, id } = useParams();
  const navigate = useNavigate();
  usePageTitle("Create Campaign");

  const [step, setStep] = useState("leads");
  const [state, setState] = useState(DEFAULT_STATE);
  const [campaignId, setCampaignId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [delayErrorOpen, setDelayErrorOpen] = useState(false);

  useEffect(() => {
    getLeadListsPromise({ limit: 100 })
      .then((res) => setLists(res.data?.data || res.data || []))
      .catch(() => {});
    getLinkedInAccounts({ limit: 100 })
      .then((res) => {
        const data = res.data?.data || res.data?.accounts || res.data || [];
        setAccounts(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    getCampaignByIdPromise(id)
      .then((res) => {
        const c = res.data;
        const settings = c.settings || {};
        setCampaignId(c.id);
        setState((prev) => ({
          ...prev,
          name: c.name || "",
          type: c.type || "MIXED",
          leadListId: c.leadListId,
          leadListIds: settings.leadListIds || (c.leadListId ? [c.leadListId] : []),
          excludeListId: settings.excludeListId || null,
          excludeOptions: settings.excludeOptions || prev.excludeOptions,
          linkedinAccountId: c.linkedinAccountId,
          linkedinAccountIds:
            c.campaignSenders?.map((s) => s.linkedinAccountId)
            || (c.linkedinAccountId ? [c.linkedinAccountId] : []),
          primaryLinkedInAccountId: c.linkedinAccountId,
          startDate: c.startDate || prev.startDate,
          endDate: c.endDate,
          timezone: settings.timezone || prev.timezone,
          activeHoursStart: settings.activeHoursStart || prev.activeHoursStart,
          activeHoursEnd: settings.activeHoursEnd || prev.activeHoursEnd,
          activeDays: settings.activeDays || prev.activeDays,
          dailyLimit: settings.dailyLimit || prev.dailyLimit,
          sequenceId: c.sequences?.[0]?.id || null,
          steps: c.sequences?.[0]?.steps || prev.steps,
        }));
      })
      .catch(() => toast.error("Failed to load campaign"));
  }, [id]);

  const persistDraft = async () => {
    const leadListIds = (state.leadListIds || []).filter(Boolean);
    const leadListId = state.leadListId || leadListIds[0] || undefined;
    const senderIds = (state.linkedinAccountIds || []).filter(Boolean);
    // Draft create may hit APIs that still require a LinkedIn account FK.
    // Prefer explicit wizard selection; otherwise provisionally attach the first sender.
    const fallbackSenderId = accounts[0]?.id || undefined;
    const linkedinAccountId =
      state.linkedinAccountId || senderIds[0] || fallbackSenderId || undefined;
    const linkedinAccountIds =
      senderIds.length > 0
        ? senderIds
        : linkedinAccountId
          ? [linkedinAccountId]
          : undefined;
    const payload = {
      name: state.name || "Untitled campaign",
      type: state.type || "MIXED",
      status: "DRAFT",
      leadListId,
      leadListIds: leadListIds.length > 0 ? leadListIds : undefined,
      linkedinAccountId,
      linkedinAccountIds,
      primaryLinkedInAccountId:
        state.primaryLinkedInAccountId || linkedinAccountId || undefined,
      startDate: state.startDate || undefined,
      endDate: state.endDate || undefined,
      activeDays: state.activeDays,
      activeHoursStart: state.activeHoursStart,
      activeHoursEnd: state.activeHoursEnd,
      timezone: state.timezone,
      dailyLimit: state.dailyLimit,
      excludeListId: state.excludeListId || undefined,
      excludeOptions: state.excludeOptions,
      sequenceId: state.sequenceId || undefined,
    };
    if (campaignId) {
      const res = await updateCampaignPromise(campaignId, payload);
      return res.data;
    }
    if (!linkedinAccountId) {
      throw new Error("Add a LinkedIn sender account before continuing");
    }
    const res = await createCampaignPromise(payload);
    const created = res.data?.data || res.data;
    setCampaignId(created.id);
    if (!state.linkedinAccountId && linkedinAccountId) {
      setState((prev) => ({
        ...prev,
        linkedinAccountId,
        linkedinAccountIds: linkedinAccountIds || [linkedinAccountId],
        primaryLinkedInAccountId: linkedinAccountId,
      }));
    }
    navigate(`/${lng}/campaigns/${created.id}/edit`, { replace: true });
    return created;
  };

  const persistSequence = async () => {
    const apiSteps = (state.steps || [])
      .filter((s) => s.type && s.type !== "START")
      .map((s, i) => sanitizeSequenceStepForApi(s, i + 1));
    if (apiSteps.length === 0) {
      throw new Error("Add at least one sequence step");
    }
    const body = {
      name: `${state.name || "Campaign"} Sequence`,
      steps: apiSteps,
      campaignId: campaignId || undefined,
    };
    if (state.sequenceId) {
      const res = await updateSequencePromise(state.sequenceId, body);
      return res.data;
    }
    const res = await createSequencePromise(body);
    const created = res.data?.data || res.data;
    setState((prev) => ({ ...prev, sequenceId: created.id }));
    if (campaignId) {
      await updateCampaignPromise(campaignId, { sequenceId: created.id });
    }
    return created;
  };

  const canContinue = () => {
    switch (step) {
      case "leads":
        return Boolean(state.name?.trim()) && (state.leadListIds?.length > 0);
      case "sequence":
        return (state.steps || []).some(
          (s) => s.type !== "START" && s.type !== "END",
        );
      case "senders":
        return (state.linkedinAccountIds || []).length > 0;
      case "schedule":
        return Boolean(state.startDate);
      case "preview":
        return true;
      default:
        return false;
    }
  };

  const goNext = async () => {
    if (step === "sequence") {
      const validation = validateStepsClient(state.steps || []);
      if (!validation.valid) {
        setDelayErrorOpen(true);
        return;
      }
    }
    setSaving(true);
    try {
      if (step === "leads") {
        await persistDraft();
      }
      if (step === "sequence") {
        await persistDraft();
        await persistSequence();
      }
      if (step === "senders" || step === "schedule") {
        await persistDraft();
      }
      const idx = STEPS.findIndex((s) => s.id === step);
      if (idx < STEPS.length - 1) {
        setStep(STEPS[idx + 1].id);
      }
    } catch (err) {
      notifyError(err, "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.id === step);
    if (idx <= 0) {
      navigate(`/${lng}/campaigns`);
      return;
    }
    setStep(STEPS[idx - 1].id);
  };

  const launch = async () => {
    setSaving(true);
    try {
      const validation = validateStepsClient(state.steps || []);
      if (!validation.valid) {
        setDelayErrorOpen(true);
        setStep("sequence");
        return;
      }
      await persistDraft();
      await persistSequence();
      await startCampaignPromise(campaignId);
      toast.success("Campaign launched");
      navigate(`/${lng}/campaigns/${campaignId}`);
    } catch (err) {
      notifyError(err, "Failed to launch");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col">
      <div className="flex items-center justify-between px-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(`/${lng}/campaigns`)}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          ← Create Campaign
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex-1 flex flex-col overflow-hidden">
        <Stepper currentStep={step} onStepClick={setStep} />

        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4">
          {step === "leads" ? <LeadsStep state={state} setState={setState} /> : null}
          {step === "sequence" ? <SequenceStep state={state} setState={setState} /> : null}
          {step === "senders" ? <SendersStep state={state} setState={setState} /> : null}
          {step === "schedule" ? <ScheduleStep state={state} setState={setState} /> : null}
          {step === "preview" ? (
            <PreviewStep
              state={state}
              lists={lists}
              accounts={accounts}
              onEdit={setStep}
            />
          ) : null}
        </div>

        <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={goBack}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm bg-white"
          >
            ← Back
          </button>
          {step === "preview" ? (
            <button
              type="button"
              disabled={saving || !canContinue()}
              onClick={launch}
              className="px-5 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
            >
              {saving ? "Launching…" : "Launch campaign >"}
            </button>
          ) : (
            <button
              type="button"
              disabled={saving || !canContinue()}
              onClick={goNext}
              className="px-5 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Continue >"}
            </button>
          )}
        </div>
      </div>

      {delayErrorOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDelayErrorOpen(false)} />
          <div className="relative bg-white rounded-2xl p-6 max-w-md w-full text-center shadow-xl">
            <div className="w-14 h-14 rounded-full bg-red-100 text-red-500 text-2xl flex items-center justify-center mx-auto mb-4">
              ×
            </div>
            <p className="text-sm text-gray-700">
              There are a few errors in the delays between the action steps in the sequence. Check
              all delays and make sure all values are not lower than 3 hours or higher than 500 days,
              then try again.
            </p>
            <button
              type="button"
              onClick={() => setDelayErrorOpen(false)}
              className="mt-5 px-6 py-2 rounded-lg bg-blue-500 text-white text-sm"
            >
              Ok
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
